import { OverviewDashboardStatus, ReadingStatus } from 'infrastructure/enums';
import {
  useOverviewDashboardFilter,
  useRpmOverviewDashboardCount,
} from 'infrastructure/hooks/rpm/overview-dashboard';
import { useTranslation } from 'react-i18next';
import useUser from 'utils/useUser';

enum AllowedRole {
  Critical = 'critical',
  Risk = 'risk',
  Priority = 'priority',
}

export const useButtons = () => {
  const { filter, updateFilter } = useOverviewDashboardFilter();
  const { isCriticalRole, isAtRiskRole, isPriorityPatientAccessRole } =
    useUser();
  const { count, isLoading } = useRpmOverviewDashboardCount(filter);
  const { t } = useTranslation();

  const { status } = filter;

  const isPriority = status === OverviewDashboardStatus.Priority;
  const isCritical = status === OverviewDashboardStatus.Critical;
  const isRisk = status === OverviewDashboardStatus.Risk;
  const isNormal = status === OverviewDashboardStatus.Normal;

  const onClick = async (newStatus: OverviewDashboardStatus) => {
    status === newStatus
      ? updateFilter({ status: undefined, page: undefined })
      : updateFilter({
          status: newStatus,
          page: undefined,
        });
  };

  let buttons = [
    {
      status: OverviewDashboardStatus.Priority,
      count: count?.priorityPatients ?? 0,
      active: isPriority,
      label: t('labels.priority'),
      bgColor: '#598BF7',
      render: true,
      role: AllowedRole.Priority,
      onClick: () => onClick(OverviewDashboardStatus.Priority),
    },
    {
      status: ReadingStatus.Critical,
      count: count?.critical ?? 0,
      active: isCritical,
      label: t('labels.critical'),
      bgColor: '#B20109',
      render: true,
      role: AllowedRole.Critical,
      onClick: () => onClick(OverviewDashboardStatus.Critical),
    },
    {
      status: ReadingStatus.Risk,
      count: count?.atRisk ?? 0,
      active: isRisk,
      label: t('labels.at-risk'),
      bgColor: '#FF7A00',
      render: true,
      role: AllowedRole.Risk,
      onClick: () => onClick(OverviewDashboardStatus.Risk),
    },
    {
      status: ReadingStatus.Normal,
      count: count?.normal ?? 0,
      active: isNormal,
      label: t('labels.normal'),
      bgColor: '#61B753',
      render: true,
      role: AllowedRole.Risk,
      onClick: () => onClick(OverviewDashboardStatus.Normal),
    },
  ];

  if (!isCriticalRole && !isAtRiskRole)
    buttons = buttons.filter((b) => b.render);
  if (isCriticalRole)
    buttons = buttons.filter((b) => b.role === AllowedRole.Critical);
  if (isAtRiskRole)
    buttons = buttons.filter((b) => b.role === AllowedRole.Risk);

  if (!isPriorityPatientAccessRole)
    buttons = buttons.filter((b) => b.role !== AllowedRole.Priority);

  return { buttons, isLoading };
};
